import React, { useState } from 'react';
import { BilingualText } from '../components/BilingualText';
import { ArrowLeft, Settings2, Palette, Globe, Mail, Zap, ShieldCheck, Link as LinkIcon, Save, Upload, CheckCircle2, AlertCircle, Clock, Lock } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
type SettingsSection = 'general' | 'branding' | 'workflow' | 'security' | 'integrations';
export function SystemSettings() {
  const [activeSection, setActiveSection] = useState<SettingsSection>('general');
  const navigate = useNavigate();
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-1109">
      <header className="bg-white px-4 py-6 border-b border-slate-200 flex items-center justify-between sticky top-0 z-10 lg:px-8" data-id="element-1110">
        <div className="flex items-center gap-4" data-id="element-1111">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1112">
            <ArrowLeft size={24} data-id="element-1113" />
          </button>
          <BilingualText en="System Settings" ar="إعدادات النظام" className="text-xl font-bold" data-id="element-1114" />
        </div>
        <button className="btn-primary py-2 px-4 text-sm" data-id="element-1115">
          <Save size={18} data-id="element-1116" />
          <span data-id="element-1117">Save All Changes</span>
        </button>
      </header>

      <main className="p-4 md:p-8 max-w-7xl mx-auto flex flex-col lg:flex-row gap-8" data-id="element-1118">
        {/* Sidebar Navigation */}
        <aside className="w-full lg:w-64 shrink-0 space-y-1" data-id="element-1119">
          <NavButton active={activeSection === 'general'} onClick={() => setActiveSection('general')} icon={Settings2} label="General" data-id="element-1120" />
          <NavButton active={activeSection === 'branding'} onClick={() => setActiveSection('branding')} icon={Palette} label="Branding" data-id="element-1121" />
          <NavButton active={activeSection === 'workflow'} onClick={() => setActiveSection('workflow')} icon={Zap} label="Workflow" data-id="element-1122" />
          <NavButton active={activeSection === 'security'} onClick={() => setActiveSection('security')} icon={ShieldCheck} label="Security" data-id="element-1123" />
          <NavButton active={activeSection === 'integrations'} onClick={() => setActiveSection('integrations')} icon={LinkIcon} label="Integrations" data-id="element-1124" />
        </aside>

        {/* Content Area */}
        <div className="flex-1 space-y-6" data-id="element-1125">
          {activeSection === 'general' && <section className="card p-6 space-y-6 animate-in fade-in duration-500" data-id="element-1126">
              <BilingualText en="General Configuration" ar="الإعدادات العامة" className="text-xl" data-id="element-1127" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1128">
                <InputGroup label="System Name" defaultValue="DAS" data-id="element-1129" />
                <InputGroup label="Default Language" type="select" options={['English', 'Arabic']} data-id="element-1130" />
                <InputGroup label="Time Zone" type="select" options={['(GMT+03:00) Riyadh', '(GMT+04:00) Dubai']} data-id="element-1131" />
                <InputGroup label="Date Format" type="select" options={['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD']} data-id="element-1132" />
              </div>
            </section>}

          {activeSection === 'branding' && <section className="card p-6 space-y-8 animate-in fade-in duration-500" data-id="element-1133">
              <BilingualText en="Company Branding" ar="هوية الشركة" className="text-xl" data-id="element-1134" />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1135">
                <div className="space-y-4" data-id="element-1136">
                  <label className="text-xs font-bold text-slate-400 uppercase" data-id="element-1137">
                    System Logo
                  </label>
                  <div className="border-2 border-dashed border-slate-200 rounded-2xl p-8 flex flex-col items-center justify-center gap-3 bg-slate-50" data-id="element-1138">
                    <img src="https://cdn.magicpatterns.com/uploads/8gwbCWtCALwvXSSg9wjHzH/ChatGPT_Image_Jul_11,_2026,_08_45_11_PM.png" className="h-16 w-auto mb-2" alt="Preview" data-id="element-1139" />
                    <button className="btn-secondary py-2 px-4 text-xs" data-id="element-1140">
                      <Upload size={14} data-id="element-1141" />
                      Change Logo
                    </button>
                  </div>
                </div>
                <div className="space-y-6" data-id="element-1142">
                  <div className="space-y-2" data-id="element-1143">
                    <label className="text-xs font-bold text-slate-400 uppercase" data-id="element-1144">
                      Primary Brand Color
                    </label>
                    <div className="flex gap-3 items-center" data-id="element-1145">
                      <div className="w-12 h-12 rounded-xl bg-[#2E3192] border-4 border-white shadow-sm" data-id="element-1146" />
                      <input type="text" defaultValue="#2E3192" className="flex-1 p-3 bg-slate-50 border border-slate-200 rounded-lg font-mono" data-id="element-1147" />
                    </div>
                  </div>
                  <InputGroup label="Email Footer Text" defaultValue="© 2026 DAS Delivery Appointment System. All rights reserved." data-id="element-1148" />
                </div>
              </div>
            </section>}

          {activeSection === 'workflow' && <section className="card p-6 space-y-6 animate-in fade-in duration-500" data-id="element-1149">
              <BilingualText en="Workflow & Automation" ar="سير العمل والأتمتة" className="text-xl" data-id="element-1150" />
              <div className="space-y-4" data-id="element-1151">
                <ToggleRow label="Require Appointment Approval" description="All supplier requests must be manually approved by warehouse staff." defaultChecked data-id="element-1152" />
                <ToggleRow label="Allow Supplier Rescheduling" description="Suppliers can change appointment times before they are approved." defaultChecked data-id="element-1153" />
                <ToggleRow label="Automatic Capacity Validation" description="System blocks bookings that exceed daily operational limits." defaultChecked data-id="element-1154" />
                <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1155">
                  <InputGroup label="Default Appointment Duration" type="select" options={['15 Minutes', '30 Minutes', '60 Minutes']} defaultValue="30 Minutes" data-id="element-1156" />
                  <InputGroup label="Max Booking Window" type="select" options={['7 Days', '14 Days', '30 Days']} defaultValue="14 Days" data-id="element-1157" />
                </div>
              </div>
            </section>}

          {activeSection === 'security' && <section className="card p-6 space-y-6 animate-in fade-in duration-500" data-id="element-1158">
              <BilingualText en="Security & Access" ar="الأمن والوصول" className="text-xl" data-id="element-1159" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1160">
                <InputGroup label="Min Password Length" type="number" defaultValue={8} data-id="element-1161" />
                <InputGroup label="Session Timeout (Minutes)" type="number" defaultValue={30} data-id="element-1162" />
                <InputGroup label="Max Login Attempts" type="number" defaultValue={5} data-id="element-1163" />
                <ToggleRow label="Two-Factor Authentication" description="Require OTP for all administrator logins." data-id="element-1164" />
              </div>
            </section>}

          {activeSection === 'integrations' && <section className="space-y-4 animate-in fade-in duration-500" data-id="element-1165">
              <BilingualText en="External Integrations" ar="التكاملات الخارجية" className="text-xl px-2" data-id="element-1166" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1167">
                <IntegrationCard name="ERP System" status="Connected" icon={LinkIcon} lastSync="5 mins ago" data-id="element-1168" />
                <IntegrationCard name="WMS Gateway" status="Connected" icon={Warehouse} lastSync="12 mins ago" data-id="element-1169" />
                <IntegrationCard name="Email (SMTP)" status="Connected" icon={Mail} lastSync="Instant" data-id="element-1170" />
                <IntegrationCard name="WhatsApp API" status="Disconnected" icon={Zap} color="text-slate-400" data-id="element-1171" />
              </div>
            </section>}
        </div>
      </main>
    </div>;
}
function NavButton({
  active,
  onClick,
  icon: Icon,
  label
}: any) {
  return <button onClick={onClick} className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${active ? 'bg-[#2E3192] text-white shadow-lg shadow-blue-900/10' : 'text-slate-600 hover:bg-white hover:text-[#2E3192]'}`} data-id="element-1172">
      <Icon size={20} data-id="element-1173" />
      <span className="font-bold text-sm" data-id="element-1174">{label}</span>
    </button>;
}
function InputGroup({
  label,
  type = 'text',
  defaultValue,
  options
}: any) {
  return <div className="space-y-2" data-id="element-1175">
      <label className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-1176">
        {label}
      </label>
      {type === 'select' ? <select className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#2E3192] transition-all" data-id="element-1177">
          {options.map((opt: string) => <option key={opt} selected={opt === defaultValue} data-id="element-1178">
              {opt}
            </option>)}
        </select> : <input type={type} defaultValue={defaultValue} className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-[#2E3192] transition-all font-medium" data-id="element-1179" />}
    </div>;
}
function ToggleRow({
  label,
  description,
  defaultChecked = false
}: any) {
  return <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-100" data-id="element-1180">
      <div className="space-y-0.5" data-id="element-1181">
        <div className="text-sm font-bold text-slate-700" data-id="element-1182">{label}</div>
        <div className="text-xs text-slate-400 max-w-md" data-id="element-1183">{description}</div>
      </div>
      <div className={`w-12 h-6 rounded-full p-1 transition-colors cursor-pointer ${defaultChecked ? 'bg-[#2E3192]' : 'bg-slate-300'}`} data-id="element-1184">
        <div className={`w-4 h-4 bg-white rounded-full transition-transform ${defaultChecked ? 'translate-x-6' : 'translate-x-0'}`} data-id="element-1185" />
      </div>
    </div>;
}
function IntegrationCard({
  name,
  status,
  icon: Icon,
  lastSync,
  color = 'text-[#2E3192]'
}: any) {
  return <div className="card p-5 flex items-center justify-between group hover:border-[#2E3192] transition-all" data-id="element-1186">
      <div className="flex items-center gap-4" data-id="element-1187">
        <div className={`p-3 rounded-xl bg-slate-50 ${color} group-hover:bg-blue-50 transition-colors`} data-id="element-1188">
          <Icon size={24} data-id="element-1189" />
        </div>
        <div data-id="element-1190">
          <div className="font-bold text-slate-800" data-id="element-1191">{name}</div>
          <div className="flex items-center gap-1.5 mt-0.5" data-id="element-1192">
            <div className={`w-1.5 h-1.5 rounded-full ${status === 'Connected' ? 'bg-green-500' : 'bg-slate-300'}`} data-id="element-1193" />
            <span className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-1194">
              {status}
            </span>
          </div>
        </div>
      </div>
      {lastSync && <div className="text-right" data-id="element-1195">
          <div className="text-[10px] font-bold text-slate-300 uppercase" data-id="element-1196">
            Last Sync
          </div>
          <div className="text-xs font-bold text-slate-500" data-id="element-1197">{lastSync}</div>
        </div>}
    </div>;
}
function Warehouse({
  size
}: {
  size: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-1198">
      <path d="M3 21V8l9-5 9 5v13" data-id="element-1199"></path>
      <path d="M13 21V9" data-id="element-1200"></path>
      <path d="M8 21v-7" data-id="element-1201"></path>
    </svg>;
}